/**
 * Folds the live WebSocket feed into ordered chevron chat items
 * (`thinkingChevron` feature).
 *
 * The live feed models one tool call as up to three events
 * (`tool_start` → `tool_input` → `tool_complete`) and carries no `toolUseId`, so
 * ordering comes from arrival and completions are matched positionally per tool
 * name — the same assumption the backend makes when emitting them.
 *
 * The append helpers mutate in place, which is safe (and intended) under Immer
 * inside the Execution slice.
 */

import { ChatItem, ToolCall } from "@/types";
import { ToolChevronEvent } from "./types";
import { collapseConsecutive } from "./grouping";

/**
 * Appends streaming assistant text onto a live `ChatItem[]`.
 *
 * Consecutive deltas merge into the trailing text item so a run of tokens forms
 * one paragraph; a delta arriving after tool activity starts a new text item,
 * which is what produces the `text → tools → text` interleaving.
 *
 * Mutates `items` in place (safe under Immer in the Redux slice).
 */
export function appendTextToChatItems(items: ChatItem[], text: string): void {
  if (!text) return;

  const last = items[items.length - 1];
  if (last && last.kind === "text") {
    last.content += text;
    return;
  }

  items.push({ kind: "text", content: text });
}

/**
 * Folds one live `AgentSystemEvent` into a `ChatItem[]`.
 *
 * Tool events for a single cluster arrive contiguously, so the trailing `tools`
 * item is always the correct target:
 *   - `tool_start`    → append a `running` call
 *   - `tool_input`    → fill in `details` on the newest detail-less call
 *   - `tool_complete` → resolve the oldest still-running call for that tool
 *
 * `agent_thought` is deliberately ignored — that text already streamed into a
 * text item, and re-rendering it inside a chevron is the duplication this
 * feature removes.
 *
 * Mutates `items` in place (safe under Immer in the Redux slice).
 */
export function appendEventToChatItems(
  items: ChatItem[],
  event: ToolChevronEvent,
): void {
  const toolName = event.toolName ?? "";

  if (event.eventType === "tool_start") {
    if (!toolName) return;
    const last = items[items.length - 1];
    const call: ToolCall = {
      toolName,
      details: event.details,
      status: "running",
    };
    if (last && last.kind === "tools") {
      last.calls.push(call);
    } else {
      items.push({ kind: "tools", calls: [call] });
    }
    return;
  }

  if (event.eventType === "tool_input") {
    if (!toolName || !event.details) return;
    const last = items[items.length - 1];
    if (!last || last.kind !== "tools") return;
    for (let i = last.calls.length - 1; i >= 0; i--) {
      if (last.calls[i].toolName === toolName && !last.calls[i].details) {
        last.calls[i].details = event.details;
        return;
      }
    }
    return;
  }

  if (event.eventType === "tool_complete") {
    if (!toolName) return;
    // Scan every tools item (not just the trailing one): a result can land
    // after the next cluster has already started.
    for (const item of items) {
      if (item.kind !== "tools") continue;
      for (const call of item.calls) {
        if (call.toolName === toolName && call.status === "running") {
          call.status = event.details === "error" ? "error" : "success";
          return;
        }
      }
    }
  }
}

/**
 * Converts a run of live `AgentSystemEvent`s into collapsed tool calls.
 *
 * The live feed models one tool call as up to three events:
 *   - `tool_start` — name only, emitted immediately
 *   - `tool_input` — human-readable `details`, emitted once args finish streaming
 *   - `tool_complete` — `details` carries the status string
 *
 * Live events carry no `toolUseId`, so completions are attributed positionally
 * per tool name (the same FIFO assumption the backend makes when emitting them).
 *
 * `agent_thought` events are ignored: that text is already rendered as a chat
 * bubble, and duplicating it into a chevron is exactly the double-render this
 * feature removes.
 */
export function systemEventsToToolCalls(
  events: ToolChevronEvent[],
): ToolCall[] {
  const calls: ToolCall[] = [];
  /** Index of the next un-completed call per tool name. */
  const nextPending: Record<string, number> = {};

  for (const event of events) {
    const toolName = event.toolName ?? "";

    switch (event.eventType) {
      case "tool_start": {
        if (!toolName) break;
        calls.push({ toolName, details: event.details, status: "running" });
        break;
      }
      case "tool_input": {
        if (!toolName || !event.details) break;
        // Attach detail to the most recent still-running call for this tool.
        for (let i = calls.length - 1; i >= 0; i--) {
          if (calls[i].toolName === toolName && !calls[i].details) {
            calls[i].details = event.details;
            break;
          }
        }
        break;
      }
      case "tool_complete": {
        if (!toolName) break;
        const startIndex = nextPending[toolName] ?? 0;
        for (let i = startIndex; i < calls.length; i++) {
          if (calls[i].toolName === toolName && calls[i].status === "running") {
            calls[i].status = event.details === "error" ? "error" : "success";
            nextPending[toolName] = i + 1;
            break;
          }
        }
        break;
      }
      default:
        // agent_thought, cycle_start, and unknown types carry no chevron state.
        break;
    }
  }

  return collapseConsecutive(calls);
}
